import { useState, useEffect, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import slotsConfig from "../SlotsConfig_Summer2026";

// ── Palette ────────────────────────────────────────────────────────────────────
const CAT_PALETTE = [
  { bg: "#EEEDFE", tc: "#3C3489" },
  { bg: "#E1F5EE", tc: "#085041" },
  { bg: "#FAEEDA", tc: "#633806" },
  { bg: "#FAECE7", tc: "#712B13" },
  { bg: "#E6F1FB", tc: "#0C447C" },
  { bg: "#FCEBEB", tc: "#791F1F" },
  { bg: "#EAF3DE", tc: "#27500A" },
  { bg: "#FBEAF0", tc: "#72243E" },
];

export { CAT_PALETTE };

// ── Helpers ────────────────────────────────────────────────────────────────────
function extractCats(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.flatMap(o => (Array.isArray(o?.categories) ? o.categories : []));
}

export function buildCatPalette(cats) {
  const map = {};
  [...new Set(cats)].sort().forEach((c, i) => {
    map[c] = CAT_PALETTE[i % CAT_PALETTE.length];
  });
  return map;
}

// Total bookable slots (exclude pathways, blockers, podium)
const TOTAL_SLOTS = slotsConfig.filter(
  s => s.type && s.type !== "blocker" && s.type !== "pathway" && s.id !== "P1"
).length;

// ── Active event ───────────────────────────────────────────────────────────────
const EVENT_ID = "66489b2f-1fa8-4307-a57d-d62a60dba217"; // Global Village 2026

// ── Hook ───────────────────────────────────────────────────────────────────────
export function useVendorData() {
  const [slots,         setSlots]         = useState({});
  const [reservedSlots, setReservedSlots] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [eventName,     setEventName]     = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      // ── Fetch event name ─────────────────────────────────────────────────
      const { data: eventData, error: eventErr } = await supabase
        .from("events")
        .select("name")
        .eq("id", EVENT_ID)
        .maybeSingle();

      if (cancelled) return;

      if (eventErr) {
        console.error("[useVendorData] event fetch failed:", eventErr);
        setError("Could not load event data. Please refresh.");
        setLoading(false);
        return;
      }

      if (eventData) setEventName(eventData.name);

      // ── Fetch vendor slots ───────────────────────────────────────────────
      // NOTE: sub_id must be selected — without it every booking in a shared
      // tile (20x20-double, 17x17-pod, etc.) collapses to the same sub-slot
      // on the client, since MapTile / SlotFloorPlan key vendors by
      // `bk.sub_id || "A"`. This was previously missing and is the actual
      // root cause of multi-vendor tiles not rendering their sub-division
      // correctly on the vendor-facing map.
      const { data, error: slotsErr } = await supabase
        .from("vendor_slots")
        .select(`
          slot_id, sub_id, reserved, tent_mode,
          vendor_applications(
            id, description, categories,
            vendors(brand_name, logo_url, instagram, facebook, website)
          )
        `)
        .eq("event_key", EVENT_ID);

      if (cancelled) return;

      if (slotsErr) {
        console.error("[useVendorData] slots fetch failed:", slotsErr);
        setError("Failed to load vendor data. Please refresh.");
        setLoading(false);
        return;
      }

      console.log("[useVendorData] rows returned:", data?.length ?? 0);

      const map = {};
      const reserved = [];

      data?.forEach(row => {
        if (row.reserved) {
          // NOTE: this still treats any reserved row (whole-tile OR a single
          // granularly-reserved sub-slot) as blocking the entire tile on the
          // vendor-facing map. Admin's SlotGrid distinguishes the two via
          // subReservations vs reservedSlots. If you have granular
          // (sub-level) reservations in vendor_slots, a tile with one
          // reserved sub and one booked vendor will currently show as fully
          // reserved here and hide the vendor. Flagging this as a separate,
          // pre-existing gap — not something introduced by this fix.
          reserved.push(row.slot_id);
          return;
        }
        const va = row.vendor_applications;
        if (!va) return;
        const v = va.vendors;
        if (!map[row.slot_id]) map[row.slot_id] = [];
        map[row.slot_id].push({
          applicationId: va.id,
          sub_id:        row.sub_id || null,
          brand_name:    v?.brand_name  || "Unknown",
          logo_url:      v?.logo_url    || null,
          instagram:     v?.instagram   || null,
          facebook:      v?.facebook    || null,
          website:       v?.website     || null,
          description:   va.description || "",
          categories:    extractCats(va.categories),
          tent_mode:     row.tent_mode,
        });
      });

      setSlots(map);
      setReservedSlots(reserved);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const allVendors = useMemo(() => {
    const list = [];
    for (const [slotId, bs] of Object.entries(slots))
      for (const b of bs) list.push({ ...b, slotId });
    return list.sort((a, b) => a.brand_name.localeCompare(b.brand_name));
  }, [slots]);

  const allCats = useMemo(
    () => [...new Set(allVendors.flatMap(v => v.categories))].sort(),
    [allVendors]
  );

  const catPalette = useMemo(() => buildCatPalette(allCats), [allCats]);

  const occupiedCount = Object.values(slots).filter(b => b.length > 0).length;
  const reservedCount = reservedSlots.length;
  const freeCount = Math.max(0, TOTAL_SLOTS - occupiedCount - reservedCount);

  return {
    slots,
    reservedSlots,
    loading,
    error,
    eventName,
    allVendors,
    allCats,
    catPalette,
    occupiedCount,
    reservedCount,
    freeCount,
  };
}